import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { MapPin, Clock, DollarSign, ExternalLink, Mail, Phone, CheckCircle2 } from 'lucide-react';
import { Grant } from '@/hooks/useGrants';
import { useGrantApplications } from '@/hooks/useGrantApplications';
import { supabase } from '@/integrations/supabase/client';

interface GrantCardProps {
  grant: Grant;
  showFullDetails?: boolean;
  onViewDetails?: () => void;
}

export const GrantCard = ({ grant, showFullDetails = true, onViewDetails }: GrantCardProps) => {
  const [user, setUser] = useState<any>(null);
  const { toggleApplication, isApplied, getApplicationDate } = useGrantApplications([grant.id]);
  
  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const appliedDate = getApplicationDate(grant.id);
  
  const formatDeadline = (dateString: string | null) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const isDeadlineSoon = (dateString: string | null) => {
    if (!dateString) return false;
    try {
      const deadline = new Date(dateString);
      const today = new Date();
      const daysUntil = Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 3600 * 24));
      return daysUntil <= 30 && daysUntil >= 0;
    } catch {
      return false;
    }
  };

  const handleApplicationToggle = async () => {
    await toggleApplication(grant.id);
  };

  return (
    <Card className="hover:shadow-sm transition-shadow">
      <CardContent className="p-2">
        <div className="space-y-1">
          {/* Compact header with title and badges */}
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-medium text-sm line-clamp-1 flex-1">
              {grant.funder_program}
            </h3>
            <div className="flex gap-1 flex-shrink-0">
              <Badge 
                variant={grant.status === 'open' ? "default" : "secondary"}
                className="text-xs px-1 py-0"
              >
                {grant.status === 'open' ? 'Open' : 'Closed'}
              </Badge>
              {grant.deadline_date && (
                <Badge 
                  variant={isDeadlineSoon(grant.deadline_date) ? "destructive" : "secondary"}
                  className="text-xs px-1 py-0"
                >
                  <Clock className="w-2.5 h-2.5 mr-0.5" />
                  {formatDeadline(grant.deadline_date)}
                </Badge>
              )}
            </div>
          </div>

          {/* Application Status Checkbox - Show for authenticated users */}
          {user && showFullDetails && (
            <div className="flex items-center gap-2 py-1">
              <Checkbox
                id={`applied-${grant.id}`}
                checked={isApplied(grant.id)}
                onCheckedChange={handleApplicationToggle}
                className="h-4 w-4"
              />
              <label 
                htmlFor={`applied-${grant.id}`} 
                className="text-xs text-muted-foreground cursor-pointer flex items-center gap-1"
              >
                <CheckCircle2 className="w-3 h-3" />
                {isApplied(grant.id) ? 'Applied' : 'Mark as Applied'}
                {appliedDate && isApplied(grant.id) && (
                  <span className="text-xs opacity-75">
                    ({new Date(appliedDate).toLocaleDateString()})
                  </span>
                )}
              </label>
            </div>
          )}

          {/* Program focus - very compact */}
          {grant.program_focus && (
            <p className="text-xs text-muted-foreground line-clamp-1">
              {grant.program_focus}
            </p>
          )}

          {/* Ultra compact info row */}
          <div className="flex gap-1 text-xs overflow-hidden">
            {grant.geography && (
              <Badge variant="outline" className="text-xs px-1 py-0 flex-shrink-0">
                <MapPin className="w-2.5 h-2.5 mr-0.5" />
                <span className="truncate max-w-16">{grant.geography}</span>
              </Badge>
            )}
            
            {grant.typical_grant_size && (
              <Badge variant="outline" className="text-xs px-1 py-0 flex-shrink-0">
                <DollarSign className="w-2.5 h-2.5 mr-0.5" />
                <span className="truncate max-w-20">{grant.typical_grant_size}</span>
              </Badge>
            )}

            {grant.eligibility_type && (
              <Badge variant="outline" className="text-xs px-1 py-0 flex-shrink-0 max-w-20">
                <span className="truncate">{grant.eligibility_type}</span>
              </Badge>
            )}
          </div>

          {showFullDetails ? (
            <>
              {/* Eligible uses - more compact */}
              {grant.eligible_use_buckets && grant.eligible_use_buckets.length > 0 && (
                <div>
                  <div className="flex flex-wrap gap-1">
                    <span className="text-xs font-medium text-muted-foreground mr-1">Uses:</span>
                    {grant.eligible_use_buckets.slice(0, 2).map((use, index) => (
                      <Badge key={index} variant="secondary" className="text-xs px-1.5 py-0.5">
                        {use}
                      </Badge>
                    ))}
                    {grant.eligible_use_buckets.length > 2 && (
                      <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                        +{grant.eligible_use_buckets.length - 2} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Notes - very compact */}
              {grant.notes && (
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {grant.notes}
                </p>
              )}

              {/* Action buttons - more compact */}
              <div className="flex gap-2 pt-1">
                {grant.application_info_url && (
                  <Button asChild size="sm" variant="outline" className="h-7 px-2">
                    <a 
                      href={grant.application_info_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Apply
                    </a>
                  </Button>
                )}

                {grant.contact_email && (
                  <Button asChild size="sm" variant="ghost" className="h-7 px-2">
                    <a 
                      href={`mailto:${grant.contact_email}`}
                      className="flex items-center gap-1"
                    >
                      <Mail className="w-3 h-3" />
                      Email
                    </a>
                  </Button>
                )}

                {grant.contact_name && grant.phone && (
                  <Button asChild size="sm" variant="ghost" className="h-7 px-2">
                    <a 
                      href={`tel:${grant.phone}`}
                      className="flex items-center gap-1"
                    >
                      <Phone className="w-3 h-3" />
                      Call
                    </a>
                  </Button>
                )}
              </div>
            </>
          ) : (
            <Button 
              onClick={onViewDetails}
              size="sm" 
              className="w-full h-7"
            >
              Sign in to view details
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};